import styled from '@emotion/styled';
import { useAtomValue, useUpdateAtom } from 'jotai/utils';
import { FC, useEffect, useMemo } from 'react';
import Category from './Category';
import Color from './Color';
import Delivery from './Delivery';
import Footer from './Footer';
import Header from './Header';
import Price from './Price';
import BottomSheet from 'components/common/BottomSheet';
import useSearchFilterRefs, { SEARCH_FILER_REF_TYPE } from 'components/search/hooks/useSearchFilterRefs';
import {
  client_search_options_atom,
  search_filter_atom,
  set_client_search_options_atom,
  set_search_filter_atom,
} from 'components/search/store/searchSheet';
import { getSearchFilterUbl, SearchFilter as SearchFilterItem } from 'util/search_utils';
import { Log, useTracker } from 'util/Tracker';

const button_click_ubl: Pick<Log, 'category' | 'object_type'> = {
  category: 'click',
  object_type: 'button',
};

interface Props {
  is_bottom_sheet_active: boolean;
  init_search_filter: SearchFilterItem;
  fixed_category_id_list?: string[];
  search_ubl: Omit<Log, 'category'>;
  focus_category?: SEARCH_FILER_REF_TYPE | null;
  onCloseSheet: () => void;
  onSearch: (search_props: SearchFilterItem) => void;
}

/**
 * 검색 필터 시트
 * @component
 */
const SearchFilterSheet: FC<Props> = ({
  onCloseSheet,
  onSearch,
  search_ubl,
  focus_category,
  fixed_category_id_list,
  init_search_filter,
  is_bottom_sheet_active,
}) => {
  const is_show_category = !fixed_category_id_list;

  const tracker = useTracker();
  const { price_ref, color_ref, category_ref, delivery_ref, moveSearchFilterSection } =
    useSearchFilterRefs(focus_category);
  const search_filter = useAtomValue(search_filter_atom);
  const setSearchFilter = useUpdateAtom(set_search_filter_atom);
  const client_search_options = useAtomValue(client_search_options_atom);
  const setClientSearchOptions = useUpdateAtom(set_client_search_options_atom);

  const search_filter_ubl = useMemo(() => {
    const { navigation, data: data_attr, ...rest } = search_ubl;
    const filter_ubl = getSearchFilterUbl(search_filter);

    return { ...rest, navigation: getNavigationUbl(navigation), data: { ...data_attr, ...filter_ubl } };
  }, [search_ubl, search_filter]);

  useEffect(() => setClientSearchOptions(init_search_filter), [init_search_filter, setClientSearchOptions]);
  useEffect(
    () => setSearchFilter({ init_search_filter, fixed_category_id_list, client_search_options }),
    [init_search_filter, fixed_category_id_list, client_search_options, setSearchFilter],
  );

  useEffect(() => {
    if (is_bottom_sheet_active) {
      const { navigation, data: data_attr, ...rest } = search_ubl;
      tracker.addLog({
        ...rest,
        navigation: getNavigationUbl(navigation),
        category: 'pageview',
        data: {
          ...getSearchFilterUbl(init_search_filter),
          ...data_attr,
        },
      });
    }
  }, [is_bottom_sheet_active, init_search_filter, search_ubl, tracker]);

  const handleCloseClick = async () => {
    tracker.addLog({
      ...search_filter_ubl,
      ...button_click_ubl,
      object_id: 'close',
    });
    onCloseSheet();
  };

  const handleSearchClick = async () => {
    tracker.addLog({
      ...search_filter_ubl,
      ...button_click_ubl,
      object_id: 'apply',
    });
    onSearch(search_filter);

    onCloseSheet();
  };

  const handleClose = (is_active: boolean) => {
    if (is_active === false) {
      onCloseSheet();
    }
  };

  return (
    <BottomSheet active={is_bottom_sheet_active} is_full_screen setIsActive={handleClose} custom_head>
      <SC.Container>
        <Header onClose={handleCloseClick} />
        {is_show_category && (
          <Category
            ref={category_ref}
            onCategoryFocus={() => moveSearchFilterSection(SEARCH_FILER_REF_TYPE.CATEGORY)}
          />
        )}
        <Color ref={color_ref} />
        <Price ref={price_ref} />
        <Delivery ref={delivery_ref} />
        <Footer
          reset_ubl={{
            ...search_filter_ubl,
            ...button_click_ubl,
            object_id: 'clear',
          }}
          onSearch={handleSearchClick}
        />
      </SC.Container>
    </BottomSheet>
  );
};

export default SearchFilterSheet;

function getNavigationUbl(navigation_prefix: string) {
  if (!navigation_prefix) {
    return '';
  }
  return `${navigation_prefix}_filter`;
}

const SC = {
  Container: styled.div`
    position: relative;
    height: 100%;
    overflow-y: scroll;
    padding-bottom: 140px;
  `,
};
